import * as React from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * SearchableSelect — a Popover + Command combobox that replaces a plain <Select>
 * when the list is long enough to need filtering.
 *
 * Props:
 *   options      – [{ value, label }]
 *   value        – currently selected value string
 *   onChange     – (value) => void
 *   placeholder  – shown when nothing is selected
 *   searchPlaceholder – shown inside the search input
 *   emptyText    – shown when search yields no results
 *   disabled     – disables the trigger
 *   loading      – shows a spinner inside the trigger
 *   className    – applied to the trigger button
 */
export function SearchableSelect({
    options = [],
    value = "",
    onChange,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    emptyText = "No results found.",
    disabled = false,
    loading = false,
    className,
}) {
    const [open, setOpen] = React.useState(false);

    const selected = options.find((o) => o.value === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled || loading}
                    className={cn(
                        "flex h-10 w-full items-center justify-between rounded-xl border border-border bg-card px-3 py-2 text-sm ring-offset-background",
                        "placeholder:text-muted-foreground",
                        "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "transition-shadow",
                        className,
                    )}
                >
                    {loading ? (
                        <span className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Loading…
                        </span>
                    ) : selected ? (
                        <span>{selected.label}</span>
                    ) : (
                        <span className="text-muted-foreground">{placeholder}</span>
                    )}
                    <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-70" />
                </button>
            </PopoverTrigger>

            <PopoverContent
                className="p-0 w-[var(--radix-popover-trigger-width)]"
                align="start"
                sideOffset={4}
            >
                <Command>
                    <CommandInput placeholder={searchPlaceholder} className="h-9" />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {options.map((opt) => (
                                <CommandItem
                                    key={opt.value}
                                    value={opt.label} /* cmdk filters on this */
                                    onSelect={() => {
                                        onChange(opt.value === value ? "" : opt.value);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            "me-2 h-4 w-4 shrink-0",
                                            value === opt.value ? "opacity-100" : "opacity-0",
                                        )}
                                    />
                                    {opt.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
